<script lang="ts">
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { Plus, FileText, ChevronDown, ChevronRight, Trash2, Pencil } from 'lucide-svelte';
  import { termsTemplatesApi, adminTermsTemplatesApi } from '$lib/api/terms';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import { QUOTE_VERTICALS, QUOTE_VERTICAL_META, TERMS_CATEGORIES, type TermsTemplate } from '$lib/types';

  const queryClient = useQueryClient();

  let expanded = $state(new Set<number>());
  let showForm = $state(false);
  let editingId = $state<number | null>(null);
  let formError = $state('');

  function emptyForm() {
    return { name: '', vertical: 'all', category: TERMS_CATEGORIES[0] as string, body: '', is_default: false };
  }
  let form = $state(emptyForm());

  const templatesQuery = createQuery(() => ({
    queryKey: ['terms-templates'],
    queryFn: () => termsTemplatesApi.list()
  }));

  const grouped = $derived.by(() => {
    const rows = templatesQuery.data?.results ?? [];
    const byCategory = new Map<string, TermsTemplate[]>();
    for (const row of rows) {
      const list = byCategory.get(row.category) ?? [];
      list.push(row);
      byCategory.set(row.category, list);
    }
    return [...byCategory.entries()].sort(([a], [b]) => a.localeCompare(b));
  });

  const saveMutation = createMutation(() => ({
    mutationFn: (body: Record<string, unknown>) =>
      editingId ? adminTermsTemplatesApi.update(editingId, body) : adminTermsTemplatesApi.create(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['terms-templates'] });
      closeForm();
    },
    onError: (err: unknown) => (formError = getErrorMessage(err))
  }));

  const deleteMutation = createMutation(() => ({
    mutationFn: (id: number) => adminTermsTemplatesApi.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['terms-templates'] }),
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  function toggle(id: number) {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    expanded = next;
  }

  function openCreate() {
    editingId = null;
    form = emptyForm();
    formError = '';
    showForm = true;
  }

  function openEdit(row: TermsTemplate) {
    editingId = row.id;
    form = { name: row.name, vertical: row.vertical, category: row.category, body: row.body, is_default: row.is_default };
    formError = '';
    showForm = true;
  }

  function closeForm() {
    showForm = false;
  }

  function submit(e: SubmitEvent) {
    e.preventDefault();
    if (!form.name.trim() || !form.body.trim()) {
      formError = 'Name and body are required';
      return;
    }
    formError = '';
    saveMutation.mutate({ ...form });
  }

  function remove(row: TermsTemplate) {
    if (!confirm(`Delete "${row.name}"? This does not affect quotations that already attached it.`)) return;
    deleteMutation.mutate(row.id);
  }
</script>

<div class="tym-page">
  <div class="mb-[18px] flex items-center">
    <div>
      <h2 class="tym-section-title">Terms Templates</h2>
      <div class="tym-section-sub">
        Reusable terms &amp; conditions clauses &middot; is-default templates auto-attach to new quotations in a
        matching vertical
      </div>
    </div>
    <span class="flex-1"></span>
    <button type="button" class="btn-primary" onclick={openCreate}>
      <Plus class="h-4 w-4" /> New template
    </button>
  </div>

  {#if templatesQuery.isLoading}
    <p class="tym-loading-state">Loading templates&hellip;</p>
  {:else if templatesQuery.isError}
    <p class="tym-error-state">{getErrorMessage(templatesQuery.error)}</p>
  {:else if grouped.length === 0}
    <p class="tym-empty-state">No terms templates yet.</p>
  {:else}
    <div class="flex max-w-[820px] flex-col gap-6">
      {#each grouped as [category, rows] (category)}
        <div>
          <div class="mb-2 text-[11px] font-semibold uppercase tracking-[.08em] text-dark-gray">
            {category.replace('_', ' ')}
          </div>
          <div class="flex flex-col gap-2.5">
            {#each rows as row (row.id)}
              <div class="tym-card px-[18px] py-[15px]">
                <div class="flex items-center gap-[13px]">
                  <button
                    type="button"
                    class="text-dark-gray hover:text-navy shrink-0"
                    onclick={() => toggle(row.id)}
                    aria-label={expanded.has(row.id) ? 'Collapse' : 'Expand'}
                  >
                    {#if expanded.has(row.id)}
                      <ChevronDown class="h-4 w-4" />
                    {:else}
                      <ChevronRight class="h-4 w-4" />
                    {/if}
                  </button>
                  <FileText class="h-4 w-4 text-dark-gray shrink-0" />
                  <div class="min-w-0 flex-1">
                    <div class="font-heading text-[14.5px] font-bold text-navy">{row.name}</div>
                    <div class="mt-0.5 flex items-center gap-1.5">
                      <span class="tym-badge tym-badge-neutral">
                        {row.vertical === 'all' ? 'All verticals' : QUOTE_VERTICAL_META[row.vertical as (typeof QUOTE_VERTICALS)[number]]?.label ?? row.vertical}
                      </span>
                      {#if row.is_default}
                        <span class="tym-badge tym-badge-success">Default</span>
                      {/if}
                    </div>
                  </div>
                  <button type="button" class="btn-secondary !h-9 shrink-0" onclick={() => openEdit(row)}>
                    <Pencil class="h-3.5 w-3.5" /> Edit
                  </button>
                  <button
                    type="button"
                    class="btn-secondary !h-9 shrink-0 !text-red-600"
                    onclick={() => remove(row)}
                    aria-label="Delete template"
                  >
                    <Trash2 class="h-3.5 w-3.5" />
                  </button>
                </div>
                {#if expanded.has(row.id)}
                  <pre class="tym-small mt-3 whitespace-pre-wrap rounded-md bg-gray-50 p-3 text-[#10223A]">{row.body}</pre>
                {/if}
              </div>
            {/each}
          </div>
        </div>
      {/each}
    </div>
  {/if}
</div>

{#if showForm}
  <Modal title={editingId ? 'Edit template' : 'New template'} onClose={closeForm} wide>
    <form class="space-y-4" onsubmit={submit}>
      <div>
        <label class="label" for="name">Name</label>
        <input id="name" class="input" bind:value={form.name} />
      </div>
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label class="label" for="vertical">Vertical</label>
          <select id="vertical" class="input" bind:value={form.vertical}>
            <option value="all">All verticals</option>
            {#each QUOTE_VERTICALS as v (v)}
              <option value={v}>{QUOTE_VERTICAL_META[v].label}</option>
            {/each}
          </select>
        </div>
        <div>
          <label class="label" for="category">Category</label>
          <select id="category" class="input" bind:value={form.category}>
            {#each TERMS_CATEGORIES as c (c)}
              <option value={c}>{c.replace('_', ' ')}</option>
            {/each}
          </select>
        </div>
      </div>
      <div>
        <label class="label" for="body">Body</label>
        <textarea id="body" class="input min-h-[180px] font-mono text-sm" bind:value={form.body}></textarea>
      </div>
      <label class="flex items-center gap-2 text-sm text-navy">
        <input type="checkbox" bind:checked={form.is_default} />
        Auto-attach to every new quotation in this vertical
      </label>

      {#if formError}<p class="text-sm text-red-600">{formError}</p>{/if}

      <div class="flex justify-end gap-2 pt-2">
        <button type="button" class="btn-secondary" onclick={closeForm}>Cancel</button>
        <button type="submit" class="btn-primary" disabled={saveMutation.isPending}>
          {saveMutation.isPending ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  </Modal>
{/if}
